import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { MatAccordion } from '@angular/material/expansion';
import { Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import OrganizationUnit from 'models/organization-unit/organization-unit.model';
import User from 'models/user/user.model';
import ArcgisList from 'models/arcgis-list/arcgis-list.model';
import EsriService from 'models/esri-service/esri-service.model';
import EsriLayerList from 'models/esri-layer-list/esri-layer-list.model';
import EsriLayer from 'models/esri-layer/esri-layer.model';
import ExternalLayer from 'models/external-layer/external-layer.model';
import { UpdateOrganizationUnitComponent } from 'modules/organization-units/update-organization-unit/update-organization-unit.component';
import { UserService } from 'services/user/user.service';
import { OrganizationUnitService } from 'services/organization-unit/organization-unit.service';
import { ArcgisService } from 'services/arcgis/arcgis.service';

@Component({
  selector: 'app-ou-layer-connector',
  templateUrl: './ou-layer-connector.component.html',
  styleUrls: ['./ou-layer-connector.component.scss'],
})
export class OuLayerConnectorComponent implements OnInit {
  @ViewChild(MatAccordion) accordion;

  public organizationUnit: OrganizationUnit = new OrganizationUnit();
  public folderList: ArcgisList = new ArcgisList();
  public serviceList: ArcgisList = new ArcgisList();
  public layerList: EsriLayerList = new EsriLayerList();
  public user: User = new User();
  public selectedFolder = '';
  public selectedService: EsriService = new EsriService();
  public selectedLayer: EsriLayer = new EsriLayer();

  private userSubscription: Subscription;
  private ouSubscription: Subscription;
  private folderListSubscription: Subscription;
  private serviceListSubscription: Subscription;
  private layerListSubscription: Subscription;

  constructor(
    public route: ActivatedRoute,
    public userService: UserService,
    public router: Router,
    public snackBar: MatSnackBar,
    public arcgisService: ArcgisService,
    public organizationUnitService: OrganizationUnitService,
    private location: Location,
    private dialog: MatDialog,
  ) { }

  async ngOnInit() {
    this.userSubscription = this.userService.getCurrentUser().subscribe((user) => this.user = user);
    this.ouSubscription = this.organizationUnitService.getOuList().subscribe((list) => {
      const fltr: OrganizationUnit[] = list.filter((ou) => ou.id === this.route.snapshot.paramMap.get('id'));
      if (fltr.length > 0) {
        this.organizationUnit = fltr[0];
      }
    });
    await this.organizationUnitService.list();

    this.folderListSubscription = this.arcgisService.getFolderList().subscribe((folderList) => this.folderList = folderList);
    this.serviceListSubscription = this.arcgisService.getServiceList().subscribe((serviceList) => this.serviceList = serviceList);
    this.layerListSubscription = this.arcgisService.getLayerList().subscribe((layerList) => this.layerList = layerList);
    await this.arcgisService.refreshFolderList();
  }

  getPath(layer: ExternalLayer) {
    return layer.url.split('/').reverse().filter((_, i) => i === 2 || i === 3).reverse().join(' / ');
  }

  async selectFolder(folder: string) {
    this.selectedFolder = folder;
    this.selectedService = new EsriService();
    this.layerList = new EsriLayerList();
    this.selectedLayer = new EsriLayer();
    await this.arcgisService.refreshServiceList(this.selectedFolder);
  }

  async selectService(service: EsriService) {
    this.layerList = new EsriLayerList();
    this.selectedLayer = new EsriLayer();
    this.selectedService = service;
    await this.arcgisService.refreshLayerList(this.selectedService);
  }

  async selectLayer(layer: EsriLayer) {
    this.selectedLayer = layer;
  }

  deleteFolder() {
    this.selectedFolder = '';
  }

  async addLayer(layer: EsriLayer) {
    const link = this.genLayerLink(this.selectedService, layer);
    let snackText = 'Hozzáadás sikerült.';
    try {
      await this.organizationUnitService.addLayer(this.organizationUnit, [link]);
    } catch (ex) {
      snackText = 'Hozzáadás nem sikerült!';
    } finally {
      this.snackBar.open(
        snackText,
        '',
        {
          duration: 2000,
        },
      );
      this.selectedLayer = new EsriLayer();
      await this.organizationUnitService.list();
    }
  }

  async removeLayer(layer: ExternalLayer) {
    let snackText = 'Eltávolítás sikerült.';
    try {
      await this.organizationUnitService.removeLayer(this.organizationUnit, layer.url);
    } catch (ex) {
      snackText = 'Eltávolítás nem sikerült!';
    } finally {
      this.snackBar.open(
        snackText,
        '',
        {
          duration: 2000
        },
      );
      this.selectedLayer = new EsriLayer();
      await this.organizationUnitService.list();
    }
  }

  genLayerLink(service: EsriService, layer: EsriLayer): string {
    return `${environment.kapuUrl}/${service.name}/${service.type}/${layer.id.length > 0 ? layer.id : '0'}`;
  }

  isLayerAddedToOu(service: EsriService, layer: EsriLayer) {
    const layerSearch: ExternalLayer[] = this.organizationUnit.externalLayers.filter((exLayer) => exLayer.url === this.genLayerLink(service, layer));
    return layerSearch.length > 0;
  }

  update() {
    const dialogRef = this.dialog.open(
      UpdateOrganizationUnitComponent,
      {
        data: {
          organizationUnit: this.organizationUnit,
        },
      },
    );
    dialogRef.afterClosed().subscribe((niceName) => {
      this.organizationUnitService.update(new OrganizationUnit({ ...this.organizationUnit, niceName })).then(() => {
        this.organizationUnitService.list();
      })
    });
  }

  back() {
    this.location.back();
  }

  async ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.ouSubscription) {
      this.ouSubscription.unsubscribe();
    }
    if (this.folderListSubscription) {
      this.folderListSubscription.unsubscribe();
    }
    if (this.serviceListSubscription) {
      this.serviceListSubscription.unsubscribe();
    }
    if (this.layerListSubscription) {
      this.layerListSubscription.unsubscribe();
    }
  }
}
